// src/components/PathParticles.tsx
import React, { useRef, useMemo, useEffect } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';

interface PathParticlesProps {
  pathPoints: THREE.Vector3[];
  count: number;
  globalOpacity: number; // ★ 全体の透明度を制御するプロパティを追加
}

interface ParticleState {
  currentSegmentIndex: number;
  progressInSegment: number;
  speed: number;
  baseColor: THREE.Color; // 基本色（透明度はここで管理しない）
  currentPosition: THREE.Vector3;
}

const PathParticles: React.FC<PathParticlesProps> = ({ pathPoints, count, globalOpacity }) => {
  const pointsRef = useRef<THREE.Points>(null);
  const numSegments = pathPoints.length - 1;

  const particlesData = useMemo<ParticleState[]>(() => {
    const arr: ParticleState[] = [];
    if (numSegments < 1) return arr;

    const resetParticle = (currentPath: THREE.Vector3[]): ParticleState => {
      const currentNumSegments = currentPath.length -1;
      const startSegmentIndex = Math.floor(Math.random() * currentNumSegments);
      const initialProgress = Math.random();
      const fromNode = currentPath[startSegmentIndex];
      const toNode = currentPath[startSegmentIndex + 1];
      const initialPosition = new THREE.Vector3().lerpVectors(fromNode, toNode, initialProgress);
      
      return {
        currentSegmentIndex: startSegmentIndex,
        progressInSegment: initialProgress,
        speed: Math.random() * 0.3 + 0.15,
        baseColor: new THREE.Color(`hsl(${Math.random() * 60 + 180}, 100%, 70%)`),
        currentPosition: initialPosition.clone(),
      };
    };
    for (let i = 0; i < count; i++) arr.push(resetParticle(pathPoints));
    return arr;
  }, [pathPoints, count, numSegments]); // pathPoints が変わるとパーティクルが再初期化される

  const positions = useMemo(() => new Float32Array(count * 3), [count]);
  const colors = useMemo(() => new Float32Array(count * 3), [count]); // RGBのみ

  useFrame((state, delta) => {
    if (!pointsRef.current || numSegments < 1 || particlesData.length === 0 || globalOpacity <= 0.01) {
      if (pointsRef.current) pointsRef.current.visible = false;
      return;
    }
    if (pointsRef.current) pointsRef.current.visible = true;

    for (let i = 0; i < particlesData.length; i++) {
      const p = particlesData[i];
      p.progressInSegment += p.speed * delta;

      if (p.progressInSegment >= 1) {
        p.progressInSegment %= 1.0;
        p.currentSegmentIndex = (p.currentSegmentIndex + 1) % numSegments;
      }

      const fromNode = pathPoints[p.currentSegmentIndex];
      const toNode = pathPoints[p.currentSegmentIndex + 1];
      
      if (!toNode) {
          p.currentPosition.copy(fromNode);
      } else {
          p.currentPosition.lerpVectors(fromNode, toNode, p.progressInSegment);
      }

      positions[i * 3 + 0] = p.currentPosition.x;
      positions[i * 3 + 1] = p.currentPosition.y;
      positions[i * 3 + 2] = p.currentPosition.z;

      colors[i * 3 + 0] = p.baseColor.r;
      colors[i * 3 + 1] = p.baseColor.g;
      colors[i * 3 + 2] = p.baseColor.b;
    }

    const posAttr = pointsRef.current.geometry.getAttribute('position') as THREE.BufferAttribute;
    posAttr.needsUpdate = true;
    const colorAttr = pointsRef.current.geometry.getAttribute('color') as THREE.BufferAttribute;
    colorAttr.needsUpdate = true;
  });

  if (numSegments < 1) return null;

  return (
    <points ref={pointsRef} visible={globalOpacity > 0.01 && pathPoints.length > 1}>
      <bufferGeometry>
        <bufferAttribute attach="attributes-position" args={[positions, 3]} />
        <bufferAttribute attach="attributes-color" args={[colors, 3]} /> {/* RGBのみ */}
      </bufferGeometry>
      <pointsMaterial
        size={3}
        vertexColors
        sizeAttenuation
        transparent // ★ 透明を有効に
        opacity={globalOpacity} // ★ ここで全体の透明度を制御
        depthWrite={false}
        blending={THREE.AdditiveBlending}
      />
    </points>
  );
};
export default PathParticles;